import React, { useEffect, useState } from 'react';
import { checkReviewed, createReview, getReviewsByEstablishment } from '../helpers/reviews';
import { API_BASE_URL } from '../helpers/api';

const ReviewSection = ({ reviews: initialReviews, establishmentId, userId, isAuthenticated }) => {
    const [reviews, setReviews] = useState(initialReviews || []);
    const [rating, setRating] = useState(0);
    const [content, setContent] = useState('');
    const [username, setUsername] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [hoverRating, setHoverRating] = useState(0);
    const [error, setError] = useState('');
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    const reviewsPerPage = 5;
    const totalPages = Math.ceil(reviews.length / reviewsPerPage);

    // Add a function to fetch latest reviews
    const fetchReviews = async () => {
        try {
            const response = await getReviewsByEstablishment(establishmentId);
            // const data = await response.json();
            setReviews(response);
        } catch (err) {
            console.error('Error fetching reviews:', err);
        }
    };

    useEffect(() => {
        const handleInteraction = () => setHasUserInteracted(true);
        window.addEventListener('mousemove', handleInteraction);
        window.addEventListener('scroll', handleInteraction);
        return () => {
            window.removeEventListener('mousemove', handleInteraction);
            window.removeEventListener('scroll', handleInteraction);
        };
    }, []);

    const getCurrentReviews = () => {
        const indexOfLastReview = currentPage * reviewsPerPage;
        const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
        return reviews.slice(indexOfFirstReview, indexOfLastReview);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (username || e.target.username.value) {
            setError('Une erreur est survenue');
            return;
        }

        if (!hasUserInteracted) return;

        const newReview = {
            rating,
            content,
            author: userId,
            establishment: establishmentId
        };

        // Check if user has left a review already
        const hasReviewd = await checkReviewed(userId, establishmentId);
        if (hasReviewd) {
            setError('Vous avez déjà posté un avis pour cet établissement. Si vous souhaitez le modifier, merci de nous contacter.');
            return;
        }

        try {
            await createReview(newReview);
            // Reset form
            setRating(0);
            setContent('');
            setError('');
            // Fetch updated reviews
            await fetchReviews();
            // Reset to first page
            setCurrentPage(1);
        } catch (err) {
            setError('Une erreur est survenue lors de la publication de votre avis.');
        }

        // await createReview(newReview);
        // setRating(0);
        // setContent('');
    };

    return (
        <div className="row mt-4 g-4">
            {/* Review Form Section */}
            <div className="col-md-4 order-md-2">
                <div className="info-card">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h3 className="m-0">Laisser un avis</h3>
                    </div>

                    {isAuthenticated ? (
                        <form onSubmit={handleSubmit} className="review-form">
                            <div className="rating-selector mb-4">
                                <label className="form-label">Votre note</label>
                                <div className="stars d-flex gap-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <i
                                            key={star}
                                            className={`bi bi-star-fill fs-3 ${star <= (hoverRating || rating) ? 'text-warning' : 'text-muted'
                                                }`}
                                            style={{
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onClick={() => setRating(star)}
                                            onMouseEnter={() => setHoverRating(star)}
                                            onMouseLeave={() => setHoverRating(0)}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="mb-2">
                                <label className="form-label">Votre expérience</label>
                                <textarea
                                    className="form-control"
                                    rows="4"
                                    placeholder="Partagez votre expérience avec cet établissement..."
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    required
                                />
                                <input
                                    type="text"
                                    style={{ display: 'none' }}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    name="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                />
                            </div>

                            {error && <span className="error">{error}</span>}

                            <button
                                type="submit"
                                className="btn btn-success w-100 py-3 mt-4"
                                disabled={!rating || !content.trim()}
                            >
                                <i className="bi bi-send-fill me-2"></i>
                                Publier l'avis
                            </button>
                        </form>
                    ) : (
                        <p>Si vous souhaitez laisser un avis à cet établissement, merci de vous <a href="/connexion-inscription">connecter</a>.</p>
                    )}
                </div>
            </div>

            {/* Reviews List Section */}
            <div className="col-md-8 order-md-1">
                <div className="info-card">
                    <h3 className="mb-4">Avis des clients</h3>

                    <div className="reviews-list">
                        {getCurrentReviews().length < 1 &&
                            <p><i>Aucun avis pour le moment.</i></p>
                        }
                        {getCurrentReviews().map(review => (
                            <div
                                key={review.id}
                                className="review-item mb-4"
                                style={{
                                    borderBottom: '1px solid rgba(0,0,0,0.1)',
                                    paddingBottom: '2rem'
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <img src={API_BASE_URL+review.author.profilePicture.url} className="review-profile-pic" alt={review.author?.username} />
                                            <h5 className="m-0">{review.author?.username}</h5>
                                        </div>
                                        <div className="stars">
                                            {[...Array(5)].map((_, i) => (
                                                <i
                                                    key={i}
                                                    className={`bi bi-star-fill ${i < review.rating ? 'text-warning' : 'text-muted'}`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <span className="text-muted small">
                                        {new Date(review.createdAt).toLocaleDateString('fr-FR')}
                                    </span>
                                </div>
                                <p className="mb-0 text-secondary">{review.content}</p>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <nav className="mt-4 d-flex justify-content-center">
                            <ul className="pagination">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    >
                                        <i className="bi bi-chevron-left"></i>
                                    </button>
                                </li>
                                {[...Array(totalPages)].map((_, index) => (
                                    <li
                                        key={index + 1}
                                        className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                                    >
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(index + 1)}
                                        >
                                            {index + 1}
                                        </button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    >
                                        <i className="bi bi-chevron-right"></i>
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReviewSection;